import { lazy, Suspense } from 'react'
import { Route, Routes } from 'react-router'

import { useLiveEventsProcessor, useNavState } from '@fv/client-components'
import type { LiveEvent } from '@fv/models'

import { apiUri } from '@/constants'
import { AdminHomePage } from '@/features/home/AdminHomePage'
import { LoadDetail } from '@/features/loads/LoadDetail'
import { useAdminContext } from '@/hooks/useAdminContext'

import NotFoundPage from './layout/NotFoundPage'

const LoadListingPage = lazy(
  async () => import('@/features/loads/LoadListingPage'),
)
const DispatchesPage = lazy(
  () => import('@/features/dispatches/DispatchesPage'),
)
const AccountRoutes = lazy(
  () => import('@/features/accounts/AdminAccountRoutes'),
)
const ReportsApp = lazy(() => import('@/features/reports/ReportsApp'))
const ProvisioningPage = lazy(
  () => import('@/features/provisioning/ProvisioningPage'),
)

const CarrierRoutes = lazy(
  () => import('@/features/carriers/AdminCarrierRoutes'),
)

const VendorRoutes = lazy(() => import('@/features/vendors/AdminVendorRoutes'))

const PricingProposalsPage = lazy(
  () => import('@/features/pricing-proposals/PricingProposalsPage'),
)

const ToolsApp = lazy(() => import('@/features/tools/ToolsApp'))

export const AdminRoutes = () => {
  const contextQuery = useAdminContext()
  const notifyRefresh = useNavState(s => s.notifyRefresh)

  function handleEvent(event: LiveEvent) {
    if (event.eventType === 'system:reset') {
      notifyRefresh()
    }
  }
  useLiveEventsProcessor({
    authenticateUri: `${apiUri}/live-events`,
    handleEvent,
    liveEventsUri: contextQuery.data?.liveEventsUri,
  })

  return (
    <Routes>
      <Route path="search/:query" element={<AdminHomePage />} />
      <Route path="/" element={<AdminHomePage />} />
      <Route
        path="dispatches"
        element={
          <Suspense>
            <DispatchesPage />
          </Suspense>
        }
      />

      <Route
        path="loads"
        element={
          <Suspense>
            <LoadListingPage />
          </Suspense>
        }
      />

      <Route
        path="loads/:loadId"
        element={
          <Suspense>
            <LoadDetail />
          </Suspense>
        }
      />

      <Route
        path="carriers/*"
        element={
          <Suspense>
            <CarrierRoutes />
          </Suspense>
        }
      />

      <Route
        path="accounts/*"
        element={
          <Suspense>
            <AccountRoutes />
          </Suspense>
        }
      />

      <Route
        path="vendors/*"
        element={
          <Suspense>
            <VendorRoutes />
          </Suspense>
        }
      />
      <Route
        path="reports/*"
        element={
          <Suspense>
            <ReportsApp />
          </Suspense>
        }
      />

      <Route
        path="tools/*"
        element={
          <Suspense>
            <ToolsApp />
          </Suspense>
        }
      />
      <Route
        path="provisioning"
        element={
          <Suspense>
            <ProvisioningPage />
          </Suspense>
        }
      />
      <Route
        path="pricing-proposals"
        element={
          <Suspense>
            <PricingProposalsPage />
          </Suspense>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}
